import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"
import "../../newcomponents/index.css"

const FilterCard = ({
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  selectAll,
  toggleSelectAll,
  selectedSurvey,
  setSelectedSurvey,
  nonRespondersOnly,
  setNonRespondersOnly,
  negativeFeedbackOnly,
  setNegativeFeedbackOnly,
  surveyAssignments,
}) => {
  const [uniqueSurveys, setUniqueSurveys] = useState([])
  const mode = useSelector(state => state.Layout.layoutMode)

  const bg = mode === "dark" ? "#252F40" : "#fff"
  const border = mode === "dark" ? "#3a4556" : "#e5e7eb"
  const text = mode === "dark" ? "#fff" : "#111827"
  const subtext = mode === "dark" ? "#9ca3af" : "#6b7280"
  const inputBg = mode === "dark" ? "#1a2332" : "#f9fafb"
  const inputBorder = mode === "dark" ? "#3a4556" : "#e5e7eb"

  useEffect(() => {
    if (surveyAssignments) {
      const extractedSurveys = Array.from(
        new Map(
          surveyAssignments.map(assignment => [
            assignment.Survey.id,
            { id: assignment.Survey.id, title: assignment.Survey.title },
          ]),
        ).values(),
      )
      setUniqueSurveys(extractedSurveys)
    }
  }, [surveyAssignments])

  const clearFilters = () => {
    setFromDate("")
    setToDate("")
    setSelectedSurvey("")
    setNonRespondersOnly(false)
    setNegativeFeedbackOnly(false)
  }

  const hasActiveFilters =
    selectedSurvey || fromDate || toDate || nonRespondersOnly || negativeFeedbackOnly

  const inputStyle = {
    backgroundColor: inputBg,
    border: `1px solid ${inputBorder}`,
    borderRadius: "8px",
    color: text,
    fontSize: "14px",
    padding: "8px 12px",
    width: "100%",
    outline: "none",
    fontFamily: "Inter, sans-serif",
  }

  return (
    <div
      style={{
        backgroundColor: bg,
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        marginBottom: "20px",
        overflow: "hidden",
        border: `1px solid ${border}`,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: `1px solid ${border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FilterAltOutlinedIcon style={{ color: "#2990DF", fontSize: "20px" }} />
          <span style={{ fontWeight: 600, fontSize: "15px", color: text }}>
            Filters
          </span>
          {hasActiveFilters && (
            <span
              style={{
                backgroundColor: "#2990DF",
                color: "#fff",
                borderRadius: "999px",
                fontSize: "11px",
                fontWeight: 600,
                padding: "2px 8px",
              }}
            >
              Active
            </span>
          )}
        </div>

        <button
          onClick={clearFilters}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "6px 14px",
            borderRadius: "8px",
            border: `1px solid ${inputBorder}`,
            backgroundColor: "transparent",
            color: subtext,
            fontSize: "13px",
            cursor: "pointer",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <CloseOutlinedIcon style={{ fontSize: "14px" }} />
          Clear
        </button>
      </div>

      {/* Body */}
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "flex-end",
        }}
      >
        {/* Survey Select */}
        <div style={{ minWidth: "200px", flex: 1 }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: subtext, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
            Survey
          </label>
          <select
            value={selectedSurvey}
            onChange={e => setSelectedSurvey(e.target.value)}
            style={inputStyle}
          >
            <option value="">All Surveys</option>
            {uniqueSurveys.map(survey => (
              <option key={survey.id} value={survey.id}>
                {survey.title}
              </option>
            ))}
          </select>
        </div>

        {/* From Date */}
        <div style={{ minWidth: "160px", flex: 1 }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: subtext, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            style={{ ...inputStyle, accentColor: "#2990DF" }}
          />
        </div>

        {/* To Date */}
        <div style={{ minWidth: "160px", flex: 1 }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: subtext, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            style={{ ...inputStyle, accentColor: "#2990DF" }}
          />
        </div>

        {/* Toggle chips — only when a survey is selected */}
        {selectedSurvey && (
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end", paddingBottom: "2px" }}>
            <button
              onClick={() => setNonRespondersOnly(p => !p)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                borderRadius: "8px",
                border: `1px solid ${nonRespondersOnly ? "#2990DF" : inputBorder}`,
                backgroundColor: nonRespondersOnly ? "#EBF5FF" : "transparent",
                color: nonRespondersOnly ? "#2990DF" : subtext,
                fontSize: "13px",
                fontWeight: nonRespondersOnly ? 600 : 400,
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
                transition: "all 0.15s",
              }}
            >
              <PeopleAltOutlinedIcon style={{ fontSize: "16px" }} />
              Non-Responders
            </button>

            <button
              onClick={() => setNegativeFeedbackOnly(p => !p)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                borderRadius: "8px",
                border: `1px solid ${negativeFeedbackOnly ? "#ef4444" : inputBorder}`,
                backgroundColor: negativeFeedbackOnly ? "#FEF2F2" : "transparent",
                color: negativeFeedbackOnly ? "#ef4444" : subtext,
                fontSize: "13px",
                fontWeight: negativeFeedbackOnly ? 600 : 400,
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
                transition: "all 0.15s",
              }}
            >
              Negative Feedback
            </button>
          </div>
        )}

        {/* Select All */}
        <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: "2px" }}>
          <button
            onClick={toggleSelectAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 18px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: selectAll ? "#6b7280" : "#2990DF",
              color: "#fff",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Inter, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            <GroupsOutlinedIcon style={{ fontSize: "18px" }} />
            {selectAll ? "Unselect All" : "Select All"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default FilterCard
